import { useState, useEffect } from 'react';
import { Building2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface CompanyLogoProps {
  logoUrl?: string | null;
  companyName: string;
  /**
   * Optional scout card id. When provided, enables cross-bucket fallback:
   * if the primary bucket (intake-uploads or scout-files) fails, we look
   * in the other bucket for a logo belonging to this scout card.
   */
  scoutCardId?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'w-10 h-10',
  md: 'w-12 h-12',
  lg: 'w-16 h-16',
};

const iconSizes = {
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

const UUID_PREFIX_RE = /^[0-9a-f]{8}-[0-9a-f]{4}/i;
const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|svg)$/i;

type Bucket = 'intake-uploads' | 'scout-files';

async function signPath(bucket: Bucket, path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, 3600);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}

/**
 * Look for an admin-uploaded logo in scout-files/logos/ that belongs to
 * the given scout card id. Admin logos are stored as `logos/{scoutCardId}.ext`.
 */
async function findAdminLogoUrl(scoutCardId: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('scout-files')
    .list('logos', { limit: 100, search: scoutCardId });
  if (error || !data) return null;
  const match = data.find(
    (f) => f.name.startsWith(scoutCardId) && IMAGE_EXT_RE.test(f.name),
  );
  if (!match) return null;
  return signPath('scout-files', `logos/${match.name}`);
}

/**
 * Look for an intake-uploaded logo in intake-uploads/{scoutCardId}/.
 */
async function findIntakeLogoUrl(scoutCardId: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('intake-uploads')
    .list(scoutCardId, {
      limit: 100,
      sortBy: { column: 'created_at', order: 'desc' },
    });
  if (error || !data) return null;
  const match = data.find((f) => IMAGE_EXT_RE.test(f.name));
  if (!match) return null;
  return signPath('intake-uploads', `${scoutCardId}/${match.name}`);
}

export const CompanyLogo = ({
  logoUrl,
  companyName,
  scoutCardId,
  size = 'md',
  className,
}: CompanyLogoProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [triedFallback, setTriedFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImageError(false);
    setTriedFallback(false);
    setSignedUrl(null);

    const resolve = async () => {
      // Primary: use the stored logo_url when present.
      if (logoUrl) {
        const isIntakeUpload = UUID_PREFIX_RE.test(logoUrl);
        const primary: Bucket = isIntakeUpload ? 'intake-uploads' : 'scout-files';
        const url = await signPath(primary, logoUrl);
        if (!cancelled && url) {
          setSignedUrl(url);
          return;
        }
        // Primary failed — fall through to cross-bucket fallback below.
      }

      // Fallback: scan the other bucket for a logo matching this scout card.
      if (scoutCardId) {
        const isIntakePrimary = logoUrl ? UUID_PREFIX_RE.test(logoUrl) : true;
        const fallbackUrl = isIntakePrimary
          ? await findAdminLogoUrl(scoutCardId)
          : await findIntakeLogoUrl(scoutCardId);
        if (!cancelled && fallbackUrl) {
          setSignedUrl(fallbackUrl);
        }
      }
    };

    resolve().catch((err) => {
      if (!cancelled) console.error('CompanyLogo resolve failed', err);
    });

    return () => {
      cancelled = true;
    };
  }, [logoUrl, scoutCardId]);

  // If the resolved URL fails to load (e.g. signed URL expired mid-render or
  // file was deleted), try the cross-bucket fallback once before giving up.
  useEffect(() => {
    if (!imageError || triedFallback || !scoutCardId) return;
    setTriedFallback(true);
    const isIntakePrimary = logoUrl ? UUID_PREFIX_RE.test(logoUrl) : true;
    const finder = isIntakePrimary ? findAdminLogoUrl : findIntakeLogoUrl;
    finder(scoutCardId).then((url) => {
      if (url) {
        setSignedUrl(url);
        setImageError(false);
      }
    });
  }, [imageError, triedFallback, scoutCardId, logoUrl]);

  const showFallback = !signedUrl || imageError;

  return (
    <div
      className={cn(
        'rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden',
        sizeClasses[size],
        className,
      )}
    >
      {showFallback ? (
        <Building2 className={cn('text-primary', iconSizes[size])} />
      ) : (
        <img
          src={signedUrl}
          alt={`${companyName} logo`}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      )}
    </div>
  );
};
